import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ProductPromiseService, ProductPromiseListItem } from '../product-promise.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';

enum Actions {
  EDIT = 'edit',
  COPY_TOKEN = 'copyToken',
  DELETE = 'delete',
  DUPLICATE = 'duplicate',
}

const ActionOptions = [
  {
    label: 'Edit',
    value: Actions.EDIT,
  },
  {
    label: 'Copy Token',
    value: Actions.COPY_TOKEN,
  },
  {
    label: 'Duplicate',
    value: Actions.DUPLICATE,
  },
  {
    label: 'Delete',
    value: Actions.DELETE,
  },
];

@Component({
  selector: 'product-promise-list',
  templateUrl: './product-promise-list.component.html',
  styleUrls: ['./product-promise-list.component.scss'],
  standalone: false,
})
export class ProductPromiseListComponent implements OnInit, OnDestroy {
  @Output() listUpdated = new EventEmitter<void>();
  destroy$ = new Subject();
  productPromises: ProductPromiseListItem[] = [];
  actionOptions = ActionOptions;
  loading = false;

  constructor(
    private productPromiseService: ProductPromiseService,
    private router: Router,
    private dialog: MatDialog,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.loadProductPromises();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  loadProductPromises() {
    this.loading = true;
    this.productPromiseService
      .list()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (promises) => {
          this.productPromises = (promises || []).sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0;
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return a.createdAt.localeCompare(b.createdAt);
          });
        },
        error: (err) => {
          console.error('Error loading product promises:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load product promises' });
          this.productPromises = [];
        },
      });
  }

  onToggleActive(promise: ProductPromiseListItem) {
    const wasActive = promise.isActive;

    promise.isActive = !wasActive;

    this.productPromiseService
      .activate(promise.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          const message = wasActive
            ? 'Product promise deactivated successfully'
            : 'Product promise activated successfully';
          this.toastService.showToast({ mode: 'success', message });
          this.listUpdated.emit();
        },
        (err) => {
          console.error(err);
          const message = wasActive ? 'Failed to deactivate product promise' : 'Failed to activate product promise';
          this.toastService.showToast({ mode: 'danger', message });
          promise.isActive = wasActive;
          this.loadProductPromises();
        },
      );
  }

  onActionOptionSelect(optionValue: Actions, promiseId: number, promiseToken?: string) {
    switch (optionValue) {
      case Actions.EDIT: {
        this.router.navigate(['/engage/product-page', promiseId]);
        break;
      }
      case Actions.COPY_TOKEN: {
        if (promiseToken) {
          navigator.clipboard.writeText(promiseToken);
          this.toastService.showToast({
            mode: 'info',
            message: 'Token copied to clipboard',
            position: 'bottom',
            duration: 4000,
            closeable: true,
          });
        }
        break;
      }
      case Actions.DUPLICATE: {
        this.loading = true;
        this.productPromiseService
          .duplicate(promiseId)
          .pipe(
            finalize(() => (this.loading = false)),
            takeUntil(this.destroy$),
          )
          .subscribe(
            () => {
              this.toastService.showToast({ mode: 'success', message: 'Product promise duplicated successfully' });
              this.loadProductPromises();
              this.listUpdated.emit();
            },
            (err) => {
              console.error(err);
              this.toastService.showToast({ mode: 'danger', message: 'Failed to duplicate product promise' });
            },
          );
        break;
      }
      case Actions.DELETE: {
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Delete product promise',
              description: 'Are you sure you want to delete this product promise?',
              confirmText: 'Delete',
              cancelText: 'Cancel',
            },
          })
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((confirmed) => {
            if (confirmed) {
              this.loading = true;
              this.productPromiseService
                .delete(promiseId)
                .pipe(
                  finalize(() => (this.loading = false)),
                  takeUntil(this.destroy$),
                )
                .subscribe(
                  () => {
                    this.toastService.showToast({ mode: 'success', message: 'Product promise deleted successfully' });
                    this.loadProductPromises();
                    this.listUpdated.emit();
                  },
                  (err) => {
                    console.error(err);
                    this.toastService.showToast({ mode: 'danger', message: 'Failed to delete product promise' });
                  },
                );
            }
          });
        break;
      }
    }
  }
}
